import { useState, useEffect } from 'react'
import { Typography, Box, CircularProgress } from '@mui/material'
import { Warning as WarningIcon } from '@mui/icons-material'

import Modal from 'components/molecules/Modal/Modal'
import { LoadingButton } from 'components/mui'
import { NumberInput } from '../../../components/NumberInput'
import './DeleteCustomImplantModal.scss'
import { enqueueSnackbar } from 'notistack'

export interface ImplantWithCount {
  deviceId: string
  deviceDescription: string
  count: number
}

interface DeleteCustomImplantModalProps {
  open: boolean
  onClose: () => void
  implant: ImplantWithCount | null
  onConfirm: (count: number) => void
}

const DeleteCustomImplantModal = ({
  open,
  onClose,
  implant,
  onConfirm,
}: DeleteCustomImplantModalProps) => {
  const [deleteCount, setDeleteCount] = useState<number>(1)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  // Reset count when modal opens with new implant
  useEffect(() => {
    if (implant) {
      setDeleteCount(1)
    }
  }, [implant])

  if (!implant) return null

  const handleConfirm = () => {
    setIsLoading(true)
    // Simulate a brief loading state
    setTimeout(() => {
      onConfirm(deleteCount)
      setIsLoading(false)
      enqueueSnackbar('Implant removed successfully', {
        variant: 'success',
      })
      onClose()
    }, 300)
  }

  const maxCount = implant.count

  return (
    <Modal
      open={open}
      onClose={onClose}
      className="delete-custom-implant-modal-wrapper"
    >
      <div className="delete-custom-implant-modal">
        <div className="header-box">
          <WarningIcon />
          <Typography variant="h3">Remove Custom Implant</Typography>
        </div>

        <div className="content-container">
          <Typography variant="h2" className="description">
            {implant.deviceDescription}
          </Typography>

          <Typography variant="body1" className="confirmation-message">
            Are you sure you want to remove this custom implant?
          </Typography>

          {maxCount > 1 && (
            <Box className="count-selector">
              <Typography variant="body1" className="count-label">
                Select quantity to remove:
              </Typography>

              <Box className="number-input-container">
                <NumberInput
                  aria-label="Quantity"
                  min={1}
                  max={maxCount}
                  value={deleteCount}
                  onChange={(event, val) => setDeleteCount(val as number)}
                />
              </Box>

              <Typography variant="body2" className="count-info">
                {deleteCount} of {maxCount} available
              </Typography>
            </Box>
          )}
        </div>

        {isLoading && (
          <div className="loading-wrapper">
            <span className="loading-text-wrapper">
              <CircularProgress size="0.8em" className="spinner" />
              <p className="loading-text">Processing</p>
            </span>
          </div>
        )}

        <div className="buttons_container">
          <LoadingButton
            disabled={isLoading}
            loading={false}
            variant="outlined"
            onClick={onClose}
          >
            Cancel
          </LoadingButton>

          <LoadingButton
            disabled={isLoading}
            loading={isLoading}
            variant="contained"
            color="error"
            onClick={handleConfirm}
          >
            Remove
          </LoadingButton>
        </div>
      </div>
    </Modal>
  )
}

export default DeleteCustomImplantModal
